/**
 * „Wieder automatisch steuern“ für Tage oder Zeiträume (`ari/ownership/release`): gibt Preis
 * und/oder Mindestnächte an die Preisautomatik zurück – mehrere Zeiträume in einem Lauf, eine
 * Meldung mit der Zahl der zurückgegebenen Tage und den gescheiterten Zeiträumen. Prüft der
 * Server; die Oberfläche bietet es nur mit `AvailabilityDistribution.Edit` an.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { problemText, toApiError, useApi } from '@/shared/api';
import { rangeChipLabel } from '../calendar/calendarFormat';

export interface ReleaseTarget {
  propertyId: string;
  from: string;
  to: string;
  rate: boolean;
  minimumStay: boolean;
}

export function useRangeRelease(automationOff = false) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (targets: ReleaseTarget[]) =>
      Promise.allSettled(targets.map((target) => api.channex.releaseAriOwnership(target))),
    onSuccess: async (results, targets) => {
      const released = results.reduce(
        (sum, result) => sum + (result.status === 'fulfilled' ? result.value.released : 0),
        0,
      );
      const failed = results.flatMap((result, index) =>
        result.status === 'rejected' ? [{ target: targets[index]!, error: result.reason }] : [],
      );
      for (const { target, error } of failed) {
        toast.error(
          `${rangeChipLabel(target.from, target.to)}: ${problemText(await toApiError(error), 'Nicht zurückgegeben.')}`,
        );
      }
      if (failed.length < targets.length) {
        toast.success(
          released === 0
            ? 'Nichts zurückgegeben – diese Tage steuerte schon die Automatik.'
            : automationOff
              ? `${released} ${released === 1 ? 'Tag' : 'Tage'} zurückgegeben – die Automatik ist für dieses Apartment aus und übernimmt erst, wenn sie eingeschaltet wird.`
              : `${released} ${released === 1 ? 'Tag' : 'Tage'} zurückgegeben – die Preisautomatik übernimmt sie.`,
        );
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['dynamic-pricing-calendar'] }),
        queryClient.invalidateQueries({ queryKey: ['dynamic-pricing-calendar-detail'] }),
        queryClient.invalidateQueries({ queryKey: ['availability-distribution'] }),
      ]);
    },
  });
}
